/**
 * Implied FX rates from the portfolio total's currency breakdown.
 *
 * No endpoint publishes FX rates directly; the portfolio total's
 * per-currency breakdown carries each currency's value both locally and in
 * base currency, so `value_base_currency / value` is the rate the payload
 * itself is using. Anything that adds money ACROSS currencies (allocation
 * %, KPI P/L sums) must go through this map — local-currency figures are
 * not additive with base-currency ones.
 */

import type { CurrencyBreakdown } from '../services/portfolioApi';

/**
 * Build a `{ currency -> rate to base }` map.
 *
 * Every currency whose local total is non-zero contributes a rate. A bucket's
 * SIGN carries no rate information: a margin balance nets the currency
 * negative, and the implied rate is the same positive ratio a funded balance
 * would give (−100,000 JPY → −5,000 HKD implies 0.05 HKD/JPY, exactly as
 * +100,000 → +5,000 does). Skipping non-positive buckets silently drops the
 * rate for every currency that nets negative, and callers then fall back to 1
 * — pricing KRW/JPY/USD amounts as if they were already in the base currency.
 *
 * A zero bucket can imply nothing, and an entry whose two sides disagree on
 * sign is a payload bug rather than a rate, so both are omitted.
 */
export function buildCurrencyRateMap(
  breakdown: CurrencyBreakdown[] | undefined | null
): Record<string, number> {
  if (!breakdown) return {};

  const rates: Record<string, number> = {};
  breakdown.forEach((entry) => {
    if (entry.value === 0) return;
    const rate = entry.value_base_currency / entry.value;
    if (Number.isFinite(rate) && rate > 0) {
      rates[entry.currency] = rate;
    }
  });
  return rates;
}

/**
 * Rate for one currency, defaulting to 1 (same-currency / unknown-currency
 * passthrough), matching how PositionTable has always converted.
 */
export function rateForCurrency(rates: Record<string, number>, currency: string): number {
  return rates[currency] || 1;
}
